import { Ban, UploadCloud } from "lucide-react";
import { cn } from "@/lib/utils";
import { BusyIndicator } from "@/components/BusyIndicator";
import { fileDropTone, useFileDrop } from "@/hooks/useFileDrop";

export function DocumentDropZone({
  accept, busy, uploading, progress, hint, onFiles,
}: {
  accept: string;
  busy?: boolean;
  uploading?: boolean;
  progress?: string | null;
  hint: string;
  onFiles: (files: File[]) => void;
}) {
  const drop = useFileDrop({ accept, busy, onFiles });

  return (
    <div>
      <button
        type="button"
        aria-disabled={busy}
        aria-busy={uploading}
        onClick={drop.openPicker}
        {...drop.zoneProps}
        className={cn(
          "flex w-full flex-col items-center justify-center gap-1 rounded-md border-2 px-4 py-8 text-center text-sm transition-colors [&_*]:pointer-events-none",
          fileDropTone({ phase: drop.phase, busy: uploading, invalid: !!drop.rejection }),
          busy && !uploading && "cursor-not-allowed opacity-60",
        )}
      >
        {uploading ? (
          <BusyIndicator label={progress ?? "Uploading…"} />
        ) : drop.phase === "blocked" ? (
          <>
            <Ban className="size-5" aria-hidden="true" />
            <span className="font-medium">These file types aren't supported</span>
          </>
        ) : (
          <>
            <UploadCloud className="size-5" aria-hidden="true" />
            <span className={cn("font-medium", !drop.rejection && "text-foreground")}>
              Drag and drop files here, or <span className="text-primary">browse</span>
            </span>
            <span className="text-xs text-muted-foreground">{hint}</span>
          </>
        )}
      </button>
      {drop.rejection && !busy && (
        <p className="mt-1.5 text-xs font-medium text-destructive" role="alert">{drop.rejection}</p>
      )}
      <input {...drop.inputProps} />
    </div>
  );
}
